import {Checkbox, Divider, Radio, Text} from "@blueprintjs/core";
import styled from "@emotion/styled";
import {useCallback, useEffect, useMemo, useRef} from "react";
import {Controller, useFormContext, UseFormSetValue} from "react-hook-form";
import {CardTypeV2, DriverInformationValue, StopInformationValue, TopStopInformationValue} from "./constants/enums";
import {COLORS, TenantPreferences} from "../../../../constants/constants";
import {
  DriverInformationThumbnail,
  StopInformationThumbnail,
  TopStopInformationThumbnail
} from "./components/Thumbnails";
import FieldsLayoutEditor, {LayoutEditableField} from "./components/FieldsLayoutEditor";
import {
  createLayoutEditableFields,
  getAvailableWidth,
  isValidLayoutEditableField
} from "./utils/fields-layout-editable.utils";
import {StyledRadioGroup} from "../../../common/StyledRadioGroup";
import {IManifestCardConfigurationsV2} from "./types/manifest-card-configurations.type";
import {extractSimplePref, usePreferenceContext} from "../../../../providers/PreferenceProvider";
import {useFeatureFlagContext} from "../../../../providers/FeatureFlagProvider";

const {
  TEXT_GRAY: COLOR_TEXT_SECONDARY,
  DARK_BLUE: COLOR_PRIMARY,
  BG_DISABLED: COLOR_DISABLED,
  TEXT_BLACK: COLOR_TEXT
} = COLORS;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 20px;
  padding-right: 10px;
`;

const Section = styled.div``;

const Header = styled(Text)`
  font-size: 16px;
  margin-bottom: 16px;
`;

const Body = styled.div`
  display: grid;
  grid-template-columns: 1fr 320px;
  grid-template-rows: auto auto;
  gap: 16px;
  & > :nth-child(3) {
    grid-column: 1 / span 2;
    grid-row: 2;
  }
`;

const OptionContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px 12px;
  grid-auto-rows: min-content;
`;

const ImageContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const Subtitle = styled.span`
  font-size: 14px;
  color: ${COLOR_TEXT_SECONDARY};
  line-height: 16px;
  margin-bottom: 20px;
`;

export const StyledCheckbox = styled(Checkbox)`
  min-width: 140px;
  height: 16px;
  margin: 0;

  input:checked ~ .bp5-control-indicator {
    background-color: ${COLOR_PRIMARY} !important;
  }

  .label-text {
    color: ${COLOR_TEXT_SECONDARY} !important;
  }

  input:disabled ~ .label-text {
    color: ${COLOR_DISABLED} !important;
  }

  input:checked ~ .label-text {
    color: ${COLOR_TEXT} !important;
  }
`;

type TFormOptions = {
  label: string;
  value: string;
};

const DRIVER_INFORMATION_OPTIONS: TFormOptions[] = [
  {
    label: "Name",
    value: DriverInformationValue.DriverName
  },
  {
    label: "Code",
    value: DriverInformationValue.DriverCode
  },
  {
    label: "Capabilities",
    value: DriverInformationValue.DriverCapabilities
  },
  {
    label: "Code + Name",
    value: DriverInformationValue.DriverCodeAndName
  },
  {
    label: "Queued Stop Hourglass",
    value: DriverInformationValue.QueuedStopHourglass
  }
];

export const STOP_INFORMATION_OPTIONS: TFormOptions[] = [
  {
    label: "Address",
    value: StopInformationValue.Address
  },
  {
    label: "Address + Zone",
    value: StopInformationValue.AddressAndZone
  },
  {
    label: "City, State, Zone",
    value: StopInformationValue.CityStateZone
  },
  {
    label: "Zone",
    value: StopInformationValue.Zone
  },
  {
    label: "ETA Time",
    value: StopInformationValue.ETATime
  },
  {
    label: "Customer Name",
    value: StopInformationValue.CustomerName
  },
  {
    label: "Stop Name",
    value: StopInformationValue.StopName
  },
  {
    label: "Phone Number",
    value: StopInformationValue.PhoneNumber
  },
  {
    label: "Alias",
    value: StopInformationValue.Alias
  },
  {
    label: "Total Pieces",
    value: StopInformationValue.TotalPieces
  },
  {
    label: "Invoice Reference",
    value: StopInformationValue.InvoiceReference
  },
  {
    label: "Service Type",
    value: StopInformationValue.ServiceType
  },
  {
    label: "Job Number",
    value: StopInformationValue.JobNumber
  },
  {
    label: "Auth",
    value: StopInformationValue.Auth
  },
  {
    label: "Route Number",
    value: StopInformationValue.RouteNumber
  }
];

const TopStopInformationOptions: {label: string; value: TopStopInformationValue}[] = [
  {
    label: "Last Completed Stop",
    value: TopStopInformationValue.LastCompletedStop
  },
  {
    label: "Next Stop in Manifest",
    value: TopStopInformationValue.NextStopInManifest
  },
  {
    label: "First Stop in Manifest",
    value: TopStopInformationValue.FirstStopInManifest
  }
];

export const getMicroCardDriverInformation = (driverInfo: DriverInformationValue[]): DriverInformationValue[] => {
  if (driverInfo.length === 0) return [];
  if (driverInfo.length === 1) {
    return [
      DriverInformationValue.DriverCapabilities,
      DriverInformationValue.DriverSkills,
      DriverInformationValue.QueuedStopHourglass
    ].includes(driverInfo[0])
      ? [DriverInformationValue.DriverCodeAndName]
      : driverInfo;
  }
  const result = driverInfo.filter(
    (item) => item === DriverInformationValue.DriverCode || item === DriverInformationValue.DriverName
  );

  return result.length === 1 ? result : [DriverInformationValue.DriverCodeAndName];
};

const PresentContinuouslyForm = () => {
  const {control, watch, setValue, getValues} = useFormContext<IManifestCardConfigurationsV2>();
  const [cardType, driverInformation, stopInformation] = watch(["cardType", "driverInformation", "stopInformation"]);
  const isCodeAndNameSelected = driverInformation.includes(DriverInformationValue.DriverCodeAndName);
  const isSkillsSelected = driverInformation.includes(DriverInformationValue.DriverSkills);
  const isCapabilitiesSelected = driverInformation.includes(DriverInformationValue.DriverCapabilities);
  const savedValueTopStopBeforeMinimalChosen = useRef<TopStopInformationValue | null>(null);
  const microCardDriverInfoCache = useRef<string[] | null>(null);
  const detailedCardDriverInfoCache = useRef<string[] | null>(null);
  const compactAndMinimalCardDriverInfoCache = useRef<string[] | null>(null);
  const {tenantPreferences} = usePreferenceContext();
  const {skills: skillsEnabled} = useFeatureFlagContext();
  const isUseETAService = useMemo(() => {
    return extractSimplePref(tenantPreferences, TenantPreferences.useEtaService, true).value;
  }, [tenantPreferences]);

  const setValueWithOptions: UseFormSetValue<IManifestCardConfigurationsV2> = useCallback(
    (name, value) => {
      setValue(name, value as any, {shouldDirty: true, shouldTouch: true, shouldValidate: true});
    },
    [setValue]
  );

  const hideStopAndTopStopInformation = useMemo<boolean>(() => {
    return cardType === CardTypeV2.Micro;
  }, [cardType]);

  const driverInformationSelectionLimit = useMemo<1 | 2>(() => {
    return cardType === CardTypeV2.Micro ? 1 : 2;
  }, [cardType]);

  const onDriverInformationChange = useCallback(
    (field: string) => {
      const isSelected = driverInformation.includes(field);
      let updatedValue;

      // Remove if already selected
      if (isSelected) {
        updatedValue = driverInformation.filter((item) => item !== field);
      } else {
        // Limit to driverInformationSelectionLimit
        if (driverInformation.length >= driverInformationSelectionLimit) return;
        updatedValue = [...driverInformation, field];
        if (field === DriverInformationValue.DriverCodeAndName) {
          updatedValue = updatedValue.filter(
            (item) =>
              ![DriverInformationValue.DriverCode, DriverInformationValue.DriverName].includes(
                item as DriverInformationValue
              )
          );
        }
      }
      if (cardType === CardTypeV2.Micro) {
        microCardDriverInfoCache.current = updatedValue;
      } else if ([CardTypeV2.Minimal, CardTypeV2.Compact].includes(cardType)) {
        compactAndMinimalCardDriverInfoCache.current = updatedValue;
      } else {
        detailedCardDriverInfoCache.current = updatedValue;
      }
      setValueWithOptions("driverInformation", updatedValue);
    },
    [driverInformation, setValueWithOptions, driverInformationSelectionLimit, cardType]
  );

  const onStopInformationChange = useCallback(
    (field: string) => {
      const selectedFields = stopInformation.map((item) => item.field);
      const isSelected = selectedFields.includes(field);
      let updatedFields;

      // Remove if already selected
      if (isSelected) {
        updatedFields = selectedFields.filter((item) => item !== field);
      } else {
        // Limit to 2 selections
        const currentLength = selectedFields.length;
        if (currentLength >= 2) return;
        updatedFields = [...selectedFields, field];
      }
      setValueWithOptions("stopInformation", createLayoutEditableFields(updatedFields, cardType));
    },
    [stopInformation, setValueWithOptions, cardType]
  );

  const onFieldLayoutChange = useCallback(
    (fields: LayoutEditableField[]) => {
      setValueWithOptions("stopInformation", fields);
    },
    [setValueWithOptions]
  );

  useEffect(() => {
    if (!isValidLayoutEditableField(stopInformation, cardType)) {
      setValueWithOptions(
        "stopInformation",
        createLayoutEditableFields(
          stopInformation.map((item) => item.field),
          cardType
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardType]);

  const topStopOptionByCardType = useMemo(() => {
    if (cardType === CardTypeV2.Minimal) {
      return TopStopInformationOptions.filter((item) => item.value === TopStopInformationValue.NextStopInManifest);
    } else {
      return TopStopInformationOptions;
    }
  }, [cardType]);

  const driverInformationOptions = useMemo(() => {
    if (skillsEnabled) {
      return [
        ...DRIVER_INFORMATION_OPTIONS,
        {
          label: "Skills",
          value: DriverInformationValue.DriverSkills
        }
      ];
    }
    return DRIVER_INFORMATION_OPTIONS;
  }, [skillsEnabled]);

  const driverInformationOptionsByCardType = useMemo(() => {
    if (cardType === CardTypeV2.Micro) {
      return driverInformationOptions.filter(
        (option) =>
          option.value !== DriverInformationValue.DriverCapabilities &&
          option.value !== DriverInformationValue.QueuedStopHourglass &&
          option.value !== DriverInformationValue.DriverSkills
      );
    } else if (cardType === CardTypeV2.Detailed) {
      return driverInformationOptions.filter((option) => option.value !== DriverInformationValue.QueuedStopHourglass);
    } else {
      return driverInformationOptions;
    }
  }, [cardType, driverInformationOptions]);

  const stopInfomationByConfiguration = useMemo(() => {
    return STOP_INFORMATION_OPTIONS.filter((opt) =>
      isUseETAService ? true : opt.value !== StopInformationValue.ETATime
    );
  }, [isUseETAService]);

  useEffect(() => {
    if (cardType === CardTypeV2.Minimal) {
      savedValueTopStopBeforeMinimalChosen.current = getValues("topStopInformation");
      setValueWithOptions("topStopInformation", TopStopInformationValue.NextStopInManifest);
    } else {
      if (savedValueTopStopBeforeMinimalChosen.current) {
        setValueWithOptions("topStopInformation", savedValueTopStopBeforeMinimalChosen.current);
        savedValueTopStopBeforeMinimalChosen.current = null;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardType]);

  useEffect(() => {
    const savedValue = getValues("driverInformation");
    if (cardType === CardTypeV2.Micro) {
      microCardDriverInfoCache.current = savedValue;
    } else if ([CardTypeV2.Minimal, CardTypeV2.Compact].includes(cardType)) {
      compactAndMinimalCardDriverInfoCache.current = savedValue;
    } else {
      detailedCardDriverInfoCache.current = savedValue;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const driverConfigByCardTypes = {
      [CardTypeV2.Micro]: () =>
        getMicroCardDriverInformation(getValues("driverInformation") as DriverInformationValue[]),
      [CardTypeV2.Detailed]: () =>
        detailedCardDriverInfoCache.current ??
        compactAndMinimalCardDriverInfoCache.current?.filter(
          (item) => item !== DriverInformationValue.QueuedStopHourglass
        ) ??
        microCardDriverInfoCache.current ??
        [],
      [CardTypeV2.Minimal]: () =>
        compactAndMinimalCardDriverInfoCache.current ??
        detailedCardDriverInfoCache.current ??
        microCardDriverInfoCache.current ??
        [],
      [CardTypeV2.Compact]: () =>
        compactAndMinimalCardDriverInfoCache.current ??
        detailedCardDriverInfoCache.current ??
        microCardDriverInfoCache.current ??
        []
    };
    const getDriverInfo = driverConfigByCardTypes[cardType];
    setValueWithOptions("driverInformation", getDriverInfo());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardType]);

  return (
    <Container>
      <Section>
        <Header>
          Driver&apos;s Information{" "}
          <Subtitle style={{marginLeft: "8px"}}>Up to {driverInformationSelectionLimit}</Subtitle>
        </Header>
        <Body>
          <OptionContainer>
            {driverInformationOptionsByCardType.map((option) => (
              <Controller
                key={option.value}
                control={control}
                name={"driverInformation"}
                render={({field: {value}}) => {
                  const isChecked = value.includes(option.value);
                  const isCodeAndNameDisabled =
                    isCodeAndNameSelected &&
                    [DriverInformationValue.DriverCode, DriverInformationValue.DriverName].includes(
                      option.value as DriverInformationValue
                    );
                  const isSkillsDisabled =
                    isCapabilitiesSelected && option.value === DriverInformationValue.DriverSkills;
                  const isCapabilitiesDisabled =
                    isSkillsSelected && option.value === DriverInformationValue.DriverCapabilities;
                  return (
                    <StyledCheckbox
                      style={{
                        gridColumn: option.value === DriverInformationValue.QueuedStopHourglass ? "span 2" : "span 1"
                      }}
                      disabled={isCodeAndNameDisabled || isSkillsDisabled || isCapabilitiesDisabled}
                      onChange={() => onDriverInformationChange(option.value)}
                      checked={isChecked}
                      data-testid={`driver-information-checkbox-${option.value}`}
                    >
                      <span className="label-text">{option.label}</span>
                    </StyledCheckbox>
                  );
                }}
              />
            ))}
          </OptionContainer>
          <ImageContainer>
            <DriverInformationThumbnail cardType={cardType} />
          </ImageContainer>
        </Body>
      </Section>
      {!hideStopAndTopStopInformation && (
        <>
          <Divider />
          <Section>
            <Header>
              Stop Information <Subtitle style={{marginLeft: "8px"}}>Up to 2</Subtitle>
            </Header>
            <Body>
              <OptionContainer>
                {stopInfomationByConfiguration.map((option) => (
                  <Controller
                    key={option.value}
                    control={control}
                    name={"stopInformation"}
                    render={({field: {value}}) => {
                      const isChecked = value.some((item) => item.field === option.value);
                      return (
                        <StyledCheckbox
                          onChange={() => onStopInformationChange(option.value)}
                          checked={isChecked}
                          data-testid={`stop-information-checkbox-${option.value}`}
                        >
                          <span className="label-text">{option.label}</span>
                        </StyledCheckbox>
                      );
                    }}
                  />
                ))}
              </OptionContainer>
              <ImageContainer>
                <StopInformationThumbnail cardType={cardType} />
              </ImageContainer>
              <FieldsLayoutEditor
                availableWidth={getAvailableWidth(cardType, stopInformation.length - 1)}
                fields={stopInformation}
                onChange={onFieldLayoutChange}
              />
            </Body>
          </Section>
          <Divider />
          <Section>
            <Header>Top Stop Information</Header>
            <Body>
              <Controller
                control={control}
                name="topStopInformation"
                defaultValue={TopStopInformationValue.LastCompletedStop}
                render={({field: {onChange, value}}) => (
                  <StyledRadioGroup onChange={onChange} selectedValue={value} horizontal>
                    {topStopOptionByCardType.map((option) => (
                      <Radio
                        key={option.label}
                        label={option.label}
                        value={option.value}
                        data-testid={`top-stop-information-radio-${option.value}`}
                      />
                    ))}
                  </StyledRadioGroup>
                )}
              />
              <ImageContainer>
                <TopStopInformationThumbnail cardType={cardType} />
              </ImageContainer>
            </Body>
          </Section>
        </>
      )}
    </Container>
  );
};

export default PresentContinuouslyForm;
